import type { CSSProperties } from "react";
import { A } from "../assets";
import { Btn, Buddy, CONTACT, Eyebrow, Icon, Item, More, Page, Peek, Stat, TK, href } from "../core";
import { BuddyChat, KornerTablet, Matrix } from "../demos";
import { DemoFilm } from "../DemoFilm";

const photo = (src: string) => ({ "--photo": `url(${src})` }) as CSSProperties;

export default function Home() {
  return (
    <Page current="/">
      {/* 1. Hero: the thesis, and the product answering in the same fold */}
      <section className="tk-hero tk-hero--ink tk-hero--photo" style={photo(A.photoRoom)}>
        <div className="tk-wrap tk-hero__grid">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Culinary intelligence for hotels and restaurants</Eyebrow>
              <h1 className="tk-h1">A menu that talks to your guests, briefs your team and <em>answers to your P&amp;L.</em></h1>
            </div>
            <p className="tk-lede">thatKorner runs the whole service on one platform. Chef Buddy answers the guest at the table. Korner Ask puts each guest's preferences in your server's hand. Chef Brain shows which dishes earn their place on the menu.</p>
            <div className="tk-row">
              <Btn to={CONTACT}>Book a demo</Btn>
              <Btn to={TK.trialUrl} kind="ghost">Start a 15-day trial</Btn>
            </div>
            <ul className="tk-ticks">
              <li>No card for the trial</li>
              <li>Works alongside your POS</li>
              <li>60+ languages, by voice</li>
            </ul>
          </div>
          <div style={{ display: "grid", justifyItems: "center", position: "relative" }}>
            <BuddyChat />
            <img className="tk-hero__buddy" src={A.chefBuddy} alt="" />
          </div>
        </div>
      </section>

      {/* 1b. Platform demo film */}
      <DemoFilm />

      {/* 2. The problem: three people, one menu, no answers */}
      <section className="tk-sec tk-sec--bone">
        <div className="tk-wrap tk-split tk-split--wide-right tk-split--top">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>The QR era, 2020 to 2025</Eyebrow>
              <h2 className="tk-h2">A flat PDF, <em>posing as service.</em></h2>
            </div>
            <p className="tk-body">The QR menu solved a 2020 problem. Six years on, the guest still reads alone, the server still guesses, and the menu is still priced by instinct. Three people depend on the same menu and none of them gets an answer from it.</p>
            <div className="tk-photo tk-photo--tilt"><img src={A.photoOldMenu} alt="A printed restaurant menu, creased and stained" loading="lazy" /></div>
          </div>
          <div className="tk-qs">
            <div className="tk-q">
              <span className="tk-q__who">The guest</span>
              <p className="tk-q__say">Is this safe for me? What is the chef proud of tonight?</p>
              <p className="tk-q__ans">Chef Buddy answers at the table, in the guest's own language, and sends the order to the kitchen. <a href={href("/chef-buddy")}>Meet Chef Buddy</a></p>
            </div>
            <div className="tk-q">
              <span className="tk-q__who">The server</span>
              <p className="tk-q__say">What did she drink last time? Who at this table has an allergy?</p>
              <p className="tk-q__ans">Korner Ask tells the sommelier before they reach the table. <a href={href("/korner-ask")}>See Korner Ask</a></p>
            </div>
            <div className="tk-q">
              <span className="tk-q__who">The F&amp;B director</span>
              <p className="tk-q__say">Which dishes are paying for the room, and which are only busy?</p>
              <p className="tk-q__ans">Chef Brain places every dish by popularity and margin, and tests a price change before you print it. <a href={href("/chef-brain")}>Open Chef Brain</a></p>
            </div>
            <div className="tk-q">
              <span className="tk-q__who">The owner</span>
              <p className="tk-q__say">Why do I pay for five systems that do not talk to each other?</p>
              <p className="tk-q__ans">Orders, tables, kitchen tickets, billing and payments run on the same platform. <a href={href("/platform")}>Tour the platform</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Audience doors */}
      <section className="tk-sec tk-sec--ink">
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>Choose your corner</Eyebrow>
            <h2 className="tk-h2">One platform. <em>Four seats at the table.</em></h2>
          </div>
          <div className="tk-grid tk-grid--4">
            <a className="tk-door tk-door--buddy" href={href("/chef-buddy")}>
              <img src={A.chefBuddy} alt="" loading="lazy" />
              <span className="tk-door__who">I am dining tonight</span>
              <span className="tk-door__name">Chef Buddy</span>
              <p>Ask the menu anything, in your own language, and order when you are ready.</p>
              <span className="tk-door__go">For guests <Icon name="arrow" /></span>
            </a>
            <a className="tk-door" style={photo(A.photoChef)} href={href("/chef-brain")}>
              <span className="tk-door__who">I run F&amp;B for a hotel or group</span>
              <span className="tk-door__name">Chef Brain</span>
              <p>See which dishes earn their place, and what a change is worth before you make it.</p>
              <span className="tk-door__go">For GMs and F&amp;B leaders <Icon name="arrow" /></span>
            </a>
            <a className="tk-door" style={photo(A.photoGuest)} href={href("/korner-ask")}>
              <span className="tk-door__who">Every guest in my room is known</span>
              <span className="tk-door__name">Korner Ask</span>
              <p>Allergies, the last bottle, the occasion. In your sommelier's hand before the greeting.</p>
              <span className="tk-door__go">For ultra-luxury rooms <Icon name="arrow" /></span>
            </a>
            <a className="tk-door" style={photo(A.photoTable)} href={href("/platform")}>
              <span className="tk-door__who">I own restaurants</span>
              <span className="tk-door__name">The platform</span>
              <p>Orders, tables, kitchen, billing and menu intelligence in one subscription.</p>
              <span className="tk-door__go">For owners and operators <Icon name="arrow" /></span>
            </a>
          </div>
        </div>
      </section>

      {/* 4. Chef Buddy */}
      <section className="tk-sec tk-sec--sand">
        <div className="tk-wrap tk-split">
          <div style={{ display: "grid", justifyItems: "center", paddingTop: 40 }}>
            <Buddy width={300} float bubble="down">
              <small>Chef Buddy</small>
              Table 12 asked for gluten-free and light. I gave them two dishes and the reason for each.
            </Buddy>
          </div>
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>For the guest</Eyebrow>
              <h2 className="tk-h2">Chef Buddy. <em>The menu, in conversation.</em></h2>
            </div>
            <p className="tk-body">The concierge inside the menu, at the table or in the room. It knows every ingredient, explains every dish the way your chef would, and takes the order through to the kitchen.</p>
            <div className="tk-grid tk-grid--2">
              <Item icon="globe" title="60+ languages, spoken or typed">A guest from Riyadh and a guest from Shanghai are both answered in their own language.</Item>
              <Item icon="shield" title="It will not guess about allergens">Answers come from a matrix your kitchen has signed. Unknown means it says so and calls a person.</Item>
            </div>
            <More to={href("/chef-buddy")}>Meet Chef Buddy</More>
          </div>
        </div>
      </section>

      {/* 5. Chef Brain */}
      <section className="tk-sec tk-sec--paper">
        <div className="tk-wrap tk-split tk-split--wide-right">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>For the GM, the F&amp;B director and the owner</Eyebrow>
              <h2 className="tk-h2">Chef Brain. <em>Every dish, on the two axes that matter.</em></h2>
            </div>
            <p className="tk-body">Popularity on one axis, contribution margin on the other. Chef Brain sorts the whole menu into four groups and attaches an instruction to each: protect, reprice, reposition, remove. Then ask it a question in plain English and it draws the answer.</p>
            <div className="tk-grid tk-grid--2">
              <Item icon="slider" title="Test a price before you print it">Elasticity read per dish from your own transactions.</Item>
              <Item icon="basket" title="See what sells with what">Pair analysis across every ticket.</Item>
            </div>
            <More to={href("/chef-brain")}>Open Chef Brain</More>
          </div>
          <div className="tk-board">
            <div className="tk-board__bar"><img src={A.kmarkInk} alt="" /><span>Chef Brain · menu matrix</span><span>Illustrative</span></div>
            <div className="tk-board__body"><Matrix /></div>
          </div>
        </div>
      </section>

      {/* 6. Korner Ask */}
      <section className="tk-sec tk-sec--luxe">
        <div className="tk-wrap tk-split tk-split--wide-right">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>For ultra-luxury rooms</Eyebrow>
              <h2 className="tk-h2">Korner Ask. <em>The memory of a great maître d', in every hand.</em></h2>
            </div>
            <p className="tk-body">The assistant on the order-taking tablet and the sommelier's handheld. Ask about the guest in front of you and get the answer before you reach the table. In a room at this level, a guest should never have to say it twice.</p>
            <div className="tk-row">
              <Btn to={href("/korner-ask")} kind="gold">See Korner Ask</Btn>
            </div>
          </div>
          <KornerTablet />
        </div>
      </section>

      {/* 7. The platform underneath */}
      <section className="tk-sec tk-sec--ink" style={{ "--bg": "var(--ink)" } as CSSProperties}>
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>The restaurant system underneath</Eyebrow>
            <h2 className="tk-h2">From the first question <em>to the month-end margin.</em></h2>
            <p className="tk-lede">The three assistants sit on a complete restaurant management platform, delivered as software you subscribe to. Run it as your whole system or alongside the point-of-sale you already have.</p>
          </div>
          <ol className="tk-flow">
            <li><em>Chef Buddy</em><b>The guest asks</b><span>QR at the table or in the room, in their language.</span></li>
            <li><em>Order desk</em><b>The order lands</b><span>Dine-in, in-room, takeaway and delivery in one queue.</span></li>
            <li><em>Kitchen tickets</em><b>The kitchen cooks</b><span>Tickets routed by station, allergies printed per seat.</span></li>
            <li><em>Korner Ask</em><b>The floor serves</b><span>Tables, reservations and each guest's preferences.</span></li>
            <li><em>Billing</em><b>The guest pays</b><span>Bills, split checks, cards and taxes per venue.</span></li>
            <li><em>Chef Brain</em><b>The menu learns</b><span>Every ticket feeds the matrix for tomorrow's decision.</span></li>
          </ol>
          <div className="tk-row" style={{ marginTop: 40 }}>
            <Btn to={href("/platform")} kind="ghost">Tour the platform</Btn>
            <More to={href("/platform", "pricing")}>See pricing, from USD 149 per outlet a month</More>
          </div>
        </div>
        <Peek />
      </section>

      {/* 8. Proof, with the basis printed next to each figure */}
      <section className="tk-sec tk-sec--bone">
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>What we can stand behind</Eyebrow>
            <h2 className="tk-h2">We print the basis <em>next to the number.</em></h2>
            <p className="tk-lede">Restaurant technology is full of large figures with no source. Ours are fewer, and each one says where it comes from.</p>
          </div>
          <div className="tk-grid tk-grid--4">
            <Stat n="15–20%" basis="Pilot-observed">Higher average checks where Chef Buddy is on the table. Results vary by venue and menu.</Stat>
            <Stat n="60+" basis="Product capability">Languages Chef Buddy answers in, by voice or text.</Stat>
            <Stat n="Zero" basis="By design">Guesses on allergens. If a dish is not on the signed matrix, the answer is a refusal and a person.</Stat>
            <Stat n="60 days" basis="Commercial term">A standalone pilot for hotels, with no POS integration needed in the first phase.</Stat>
          </div>
          <p className="tk-fine" style={{ marginTop: 32 }}>Pilots are running with hotel and restaurant operators in the UAE and India. We name them when their agreements allow it, and not before.</p>
        </div>
      </section>

      {/* 9. Who built it */}
      <section className="tk-sec tk-sec--navy">
        <div className="tk-wrap tk-split tk-split--top">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Who built it</Eyebrow>
              <h2 className="tk-h2">Built by the people who used to sit <em>on your side of the table.</em></h2>
            </div>
            <p className="tk-body">thatKorner was founded by a hotel-group technology and security leader after a dinner at a corner table that service forgot. The platform is built to pass the security review he used to run, and to serve the kind of room he used to equip.</p>
            <More to={href("/trust")}>Read how we handle trust and security</More>
          </div>
          <div className="tk-stack tk-stack--lg">
            <div className="tk-person">
              <span className="tk-person__mono">VS</span>
              <div>
                <b>Vikalp Shrivastava</b><i>Founder and Managing Director</i>
                <p>More than 24 years in hospitality technology and security across 40+ ultra-luxury properties. Formerly Group Technology Head and CISO at Kerzner International (Atlantis, One&amp;Only, SIRO), with earlier roles at Wynn Resorts Macau, Melco Crown Entertainment, Radware and IBM. A winner of the IDC CISO Excellence Awards 2025, UAE.</p>
              </div>
            </div>
            <div className="tk-person">
              <span className="tk-person__mono">RS</span>
              <div>
                <b>Rama Shrivastava</b><i>Chief Executive Officer and co-founder</i>
                <p>Leads the company and its customer relationships across the GCC and India.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Page>
  );
}
